import type { AppConfig, ScheduleMeta, ScheduleResponse, VenueInfo, WatchesResponse } from '../shared/types.js';

// The server's copy of this email's watched slots — what the emailer acts
// on, and what the browser mirrors after a reload.
export async function fetchWatches(email: string): Promise<WatchesResponse> {
  const response = await fetch(`/api/watches?email=${encodeURIComponent(email)}`);
  if (!response.ok) {
    const body = await response.json().catch(() => null);
    throw new Error(body?.error || `HTTP ${response.status}`);
  }
  return response.json();
}

export async function fetchConfig(): Promise<AppConfig> {
  const response = await fetch('/api/config');
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  return response.json();
}

// Cheap poll: just the server snapshot's timestamp, so the client can tell
// whether a full /api/schedule download is worth doing.
export async function fetchScheduleMeta(): Promise<ScheduleMeta> {
  const response = await fetch('/api/schedule/meta');
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  return response.json();
}

export async function fetchVenues(): Promise<VenueInfo[]> {
  const response = await fetch('/api/venues');
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  return response.json();
}

// Serves the server's latest scraped snapshot as-is — the browser never
// triggers scraping (see server/scheduleCache.ts).
export async function fetchSchedule(): Promise<ScheduleResponse> {
  const response = await fetch('/api/schedule');
  if (!response.ok) {
    const body = await response.json().catch(() => null);
    throw new Error(body?.error || `HTTP ${response.status}`);
  }
  return response.json();
}

// "My reservation" marks, stored per email like watches (visual only, no
// emails) — so they survive cleared browser storage and follow the email
// across devices. Same {email, slots} wire shape as watches.
export async function fetchReservations(email: string): Promise<WatchesResponse> {
  const response = await fetch(`/api/reservations?email=${encodeURIComponent(email)}`);
  if (!response.ok) {
    const body = await response.json().catch(() => null);
    throw new Error(body?.error || `HTTP ${response.status}`);
  }
  return response.json();
}

export async function pushReservations(email: string, slots: string[]): Promise<WatchesResponse> {
  const response = await fetch('/api/reservations', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, slots })
  });
  if (!response.ok) {
    const body = await response.json().catch(() => null);
    throw new Error(body?.error || `HTTP ${response.status}`);
  }
  return response.json();
}

// Registers this email's full current watch set with the server so the
// background watcher can email freed slots even with no browser open.
// Returns the set the server actually kept — it drops slots that aren't
// booked/upcoming — so the caller can mirror that back.
export async function pushWatches(email: string, slots: string[]): Promise<WatchesResponse> {
  const response = await fetch('/api/watches', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, slots })
  });
  if (!response.ok) {
    const body = await response.json().catch(() => null);
    throw new Error(body?.error || `HTTP ${response.status}`);
  }
  return response.json();
}
